// The /settings/* layout route component (docs/09 §4 settings shell): it
// wraps every settings screen in the SettingsShell chrome — the secondary
// settings sidebar (Organization / Team / Agent / Personal headed sections
// derived from the shell nav registry) with the matched route's screen in
// the content pane. Row navigation rides shellNavigationSearch so agent scope
// stays canonical (`?agent=` only) while team-bound rows can receive the
// resolved working team. No settings search input until the row count warrants
// one; the ⌘K palette already indexes every settings route.
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";

import { useCreditBilling } from "../../lib/billing";
import { useIsLicensed } from "../../lib/licenseQuery";
import { useNumberProvisioning } from "../../lib/phoneNumbers";
import {
  parseShellPath,
  settingsNavSections,
  shellNavigationSearch,
} from "../../lib/shellNavigation";
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import { SettingsShell, type SettingsNavSectionItem } from "../../ui";

export function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedWorkingTeamId = useResolvedWorkingTeamId();
  const creditBilling = useCreditBilling();
  const isLicensed = useIsLicensed();
  const numberProvisioning = useNumberProvisioning();
  const routeState = parseShellPath(location.pathname);

  const sections: SettingsNavSectionItem[] = settingsNavSections({
    includeHostedOnly: creditBilling.enabled,
    includeLicensed: isLicensed,
    includeNumberProvisioning: numberProvisioning.enabled,
  }).map((section) => ({
    heading: section.heading,
    rows: section.entries.map((entry) => ({
      id: entry.id,
      isActive:
        entry.settingsTab !== undefined &&
        routeState.settingsTab === entry.settingsTab,
      label: entry.settingsRowLabel ?? entry.label,
      onSelect: () => {
        void navigate({
          search: shellNavigationSearch(
            entry.to,
            location.pathname,
            undefined,
            resolvedWorkingTeamId,
          ),
          to: entry.to,
        });
      },
    })),
  }));

  return (
    <SettingsShell sections={sections}>
      <Outlet />
    </SettingsShell>
  );
}
